// ===============================================
// ❓ FAQ SECTION - ACTUALIZADO PARA ADMINDASHBOARD
// ===============================================

import React, { useState, useEffect } from 'react';
import { 
  Search, 
  ChevronDown, 
  ChevronUp,
  Star,
  ThumbsUp,
  ThumbsDown,
  BookOpen,
  Tag,
  Clock,
  Eye,
  Filter,
  Plus,
  Edit3,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Lightbulb,
  Wrench,
  Settings,
  FileText,
  MessageCircle,
  TrendingUp,
  User,
  Calendar,
  Award,
  Target,
  Zap
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useI18n } from '../../hooks/useI18n';

// ===============================================
// 🔧 INTERFACES ACTUALIZADAS
// ===============================================

// ✅ INTERFACE COMPATIBLE CON ADMINDASHBOARD
interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: 'diagnostic' | 'maintenance' | 'repair' | 'parts' | 'installation' | 'general';
  motor?: string;
  configuration?: string;
  component?: string;
  tags: string[];
  views: number;
  helpful_votes: number;
  not_helpful_votes: number;
  created_at: string;
  updated_at: string;
  created_by: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimated_time?: string;
  related_parts?: string[];
  attachments?: string[];
  is_featured: boolean;
  is_active: boolean;
  order: number;
  search_keywords: string[];
  helpfulness?: number;
}

interface FAQCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  count: number;
}

interface FAQFilters {
  category: string;
  motor: string;
  difficulty: string;
  component: string;
  status: string;
  sort: 'relevance' | 'views' | 'helpful' | 'recent';
}

// ===============================================
// 📊 COMPONENTE PRINCIPAL
// ===============================================

const FAQSection: React.FC = () => {
  const { isDark } = useTheme();
  const { t } = useI18n();
  const [faqs, setFAQs] = useState<FAQItem[]>([]);
  const [filteredFAQs, setFilteredFAQs] = useState<FAQItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FAQFilters>({
    category: 'all',
    motor: 'all',
    difficulty: 'all',
    component: 'all',
    status: 'active',
    sort: 'relevance'
  });

  // ===============================================
  // 🎨 CLASES DE ESTILO Y CATEGORÍAS
  // ===============================================

  const cardClasses = `rounded-lg shadow-md transition-all duration-200 ${
    isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
  }`;

  const textClasses = isDark ? 'text-white' : 'text-gray-800';
  const subTextClasses = isDark ? 'text-gray-400' : 'text-gray-600';
  const borderClasses = isDark ? 'border-gray-600' : 'border-gray-300';
  const inputClasses = `w-full px-3 py-2 border rounded-lg ${
    isDark 
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  } focus:ring-2 focus:ring-blue-500 focus:border-transparent`;

  const categories: FAQCategory[] = [
    {
      id: 'diagnostic',
      name: t('faq.categories.diagnostic', 'Diagnóstico'),
      description: t('faq.categories.diagnosticDesc', 'Identificación de problemas'),
      icon: Target,
      color: 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-300',
      count: 0
    },
    {
      id: 'maintenance', 
      name: t('faq.categories.maintenance', 'Mantenimiento'),
      description: t('faq.categories.maintenanceDesc', 'Cuidado preventivo'),
      icon: Wrench,
      color: 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-300',
      count: 0
    },
    {
      id: 'repair',
      name: t('faq.categories.repair', 'Reparación'),
      description: t('faq.categories.repairDesc', 'Solución de problemas'),
      icon: Settings,
      color: 'text-blue-600 bg-blue-100 dark:bg-blue-900 dark:text-blue-300',
      count: 0
    },
    {
      id: 'parts',
      name: t('faq.categories.parts', 'Piezas'),
      description: t('faq.categories.partsDesc', 'Componentes y repuestos'),
      icon: Zap,
      color: 'text-purple-600 bg-purple-100 dark:bg-purple-900 dark:text-purple-300',
      count: 0
    },
    {
      id: 'installation',
      name: t('faq.categories.installation', 'Instalación'),
      description: t('faq.categories.installationDesc', 'Procedimientos de montaje'),
      icon: Award,
      color: 'text-indigo-600 bg-indigo-100 dark:bg-indigo-900 dark:text-indigo-300',
      count: 0
    },
    {
      id: 'general',
      name: t('faq.categories.general', 'General'),
      description: t('faq.categories.generalDesc', 'Información general'),
      icon: BookOpen,
      color: 'text-gray-600 bg-gray-100 dark:bg-gray-900 dark:text-gray-300',
      count: 0
    }
  ];

  // ===============================================
  // 📡 FUNCIONES DE API
  // ===============================================

  const loadFAQs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // ✅ CARGAR DESDE LA MISMA API QUE ADMINDASHBOARD
      const response = await fetch('/api/admin/faqs', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('kikai_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error loading FAQs: ${response.status}`);
      }
      
      const data = await response.json();
      
      // ✅ FILTRAR SOLO FAQs ACTIVAS Y NORMALIZAR DATOS
      const activeFAQs = data.faqs?.filter((faq: FAQItem) => faq.is_active).map((faq: FAQItem) => ({
        ...faq,
        tags: Array.isArray(faq.tags) ? faq.tags : [],
        search_keywords: Array.isArray(faq.search_keywords) ? faq.search_keywords : [],
        related_parts: Array.isArray(faq.related_parts) ? faq.related_parts : []
      })) || [];
      
      setFAQs(activeFAQs);
      setFilteredFAQs(activeFAQs);
      
    } catch (error) {
      console.error('Error loading FAQs:', error);
      setError(error instanceof Error ? error.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const incrementViewCount = async (faqId: string) => {
    try {
      await fetch(`/api/admin/faqs/${faqId}/view`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('kikai_token')}`
        }
      });
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const voteFAQ = async (faqId: string, helpful: boolean) => {
    try {
      await fetch(`/api/admin/faqs/${faqId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('kikai_token')}`
        },
        body: JSON.stringify({ helpful })
      });
      
      // Actualizar contador local
      setFAQs(prev => prev.map(faq => 
        faq.id === faqId 
          ? { 
              ...faq, 
              helpful_votes: helpful ? (faq.helpful_votes || 0) + 1 : (faq.helpful_votes || 0),
              not_helpful_votes: !helpful ? (faq.not_helpful_votes || 0) + 1 : (faq.not_helpful_votes || 0)
            }
          : faq
      ));
    } catch (error) {
      console.error('Error voting FAQ:', error);
    }
  };

  // ===============================================
  // 🔍 FUNCIONES DE FILTRADO Y BÚSQUEDA
  // ===============================================

  const filterFAQs = () => {
    let filtered = [...faqs];

    // Filtro por búsqueda
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(faq => 
        faq.question.toLowerCase().includes(searchLower) ||
        faq.answer.toLowerCase().includes(searchLower) ||
        (faq.tags && Array.isArray(faq.tags) && faq.tags.some(tag => tag.toLowerCase().includes(searchLower))) ||
        (faq.search_keywords && Array.isArray(faq.search_keywords) && faq.search_keywords.some(keyword => keyword.toLowerCase().includes(searchLower))) ||
        (faq.motor && faq.motor.toLowerCase().includes(searchLower)) ||
        (faq.component && faq.component.toLowerCase().includes(searchLower)) ||
        (faq.configuration && faq.configuration.toLowerCase().includes(searchLower))
      );
    }

    // Filtros específicos
    if (filters.category !== 'all') {
      filtered = filtered.filter(faq => faq.category === filters.category);
    }
    
    if (filters.motor !== 'all') {
      filtered = filtered.filter(faq => faq.motor === filters.motor);
    }
    
    if (filters.difficulty !== 'all') {
      filtered = filtered.filter(faq => faq.difficulty === filters.difficulty);
    }
    
    if (filters.component !== 'all') {
      filtered = filtered.filter(faq => faq.component === filters.component);
    }

    // Ordenamiento
    switch (filters.sort) {
      case 'views':
        filtered.sort((a, b) => b.views - a.views);
        break;
      case 'helpful':
        filtered.sort((a, b) => b.helpful_votes - a.helpful_votes);
        break;
      case 'recent':
        filtered.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
        break;
      default:
        // Relevancia: featured first, then by order, then by helpful votes
        filtered.sort((a, b) => {
          if (a.is_featured && !b.is_featured) return -1;
          if (!a.is_featured && b.is_featured) return 1;
          if (a.order !== b.order) return a.order - b.order;
          return b.helpful_votes - a.helpful_votes;
        });
    }

    setFilteredFAQs(filtered);
  };

  // ===============================================
  // 🎯 FUNCIONES DE FAQ
  // ===============================================

  const toggleFAQ = async (faqId: string) => {
    if (expandedFAQ === faqId) {
      setExpandedFAQ(null);
    } else {
      setExpandedFAQ(faqId);
      await incrementViewCount(faqId);
      
      // Incrementar contador local
      setFAQs(prev => prev.map(faq => 
        faq.id === faqId ? { ...faq, views: (faq.views || 0) + 1 } : faq
      ));
    }
  };

  // ===============================================
  // 🔄 EFECTOS
  // ===============================================

  useEffect(() => {
    loadFAQs();
  }, []);

  useEffect(() => {
    filterFAQs();
  }, [searchTerm, filters, faqs]);

  // ===============================================
  // 🎨 FUNCIONES DE UTILIDAD
  // ===============================================

  const getDifficultyColor = (difficulty: string) => {
    const colors = {
      beginner: isDark ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800',
      intermediate: isDark ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      advanced: isDark ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'
    };
    return colors[difficulty] || colors.beginner;
  };

  const getCategoryIcon = (category: string) => {
    const categoryData = categories.find(cat => cat.id === category);
    return categoryData?.icon || BookOpen;
  };

  const getCategoryColor = (category: string) => {
    const categoryData = categories.find(cat => cat.id === category);
    return categoryData?.color || 'text-gray-600 bg-gray-100';
  };

  const getUniqueValues = (field: keyof FAQItem) => {
    const values = faqs
      .map(faq => faq[field])
      .filter(value => value && value !== '' && value !== null && value !== undefined)
      .filter((value, index, self) => self.indexOf(value) === index);
    return values.sort();
  };

  const getHelpfulnessPercentage = (faq: FAQItem) => {
    const helpfulVotes = faq.helpful_votes || 0;
    const notHelpfulVotes = faq.not_helpful_votes || 0;
    const total = helpfulVotes + notHelpfulVotes;
    return total > 0 ? Math.round((helpfulVotes / total) * 100) : 0;
  };

  // ===============================================
  // 🎯 RENDERIZADO PRINCIPAL
  // ===============================================

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`${cardClasses} p-6 mb-6`}>
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Lightbulb className="w-6 h-6 text-yellow-600" />
              <h1 className={`text-2xl font-bold ${textClasses}`}>
                {t('faq.title', 'Preguntas Frecuentes')}
              </h1>
            </div>
            <p className={subTextClasses}>
              {t('faq.description', 'Encuentra respuestas rápidas a las consultas más comunes sobre motores Caterpillar')}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border bg-blue-600 text-white  ${borderClasses} ${
                isDark 
                  ? 'hover:bg-blue-700' 
                  : 'hover:bg-gray-50'
              } transition-colors`}
            >
              <Filter className="w-4 h-4" />
              {t('faq.filters', 'Filtros')}
            </button>
            
            <button
              onClick={loadFAQs}
              disabled={loading}
              className={`p-2 rounded-lg border bg-blue-600 text-white ${borderClasses} ${
                isDark 
                  ? 'hover:bg-blue-700' 
                  : 'hover:bg-gray-50'
              } transition-colors`}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Búsqueda principal */}
        <div className="mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('faq.searchPlaceholder', 'Buscar en preguntas, respuestas, tags, motor, componente...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${inputClasses}`}
            />
          </div>
        </div>

        {/* Panel de filtros expandible */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className={`block text-sm font-medium ${textClasses} mb-2`}>
                {t('faq.category', 'Categoría')}
              </label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
                className={inputClasses}
              >
                <option value="all">{t('faq.allCategories', 'Todas las categorías')}</option>
                <option value="diagnostic">{t('faq.categories.diagnostic', 'Diagnóstico')}</option>
                <option value="maintenance">{t('faq.categories.maintenance', 'Mantenimiento')}</option>
                <option value="repair">{t('faq.categories.repair', 'Reparación')}</option>
                <option value="parts">{t('faq.categories.parts', 'Piezas')}</option>
                <option value="installation">{t('faq.categories.installation', 'Instalación')}</option>
                <option value="general">{t('faq.categories.general', 'General')}</option>
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${textClasses} mb-2`}>
                {t('faq.difficulty', 'Dificultad')}
              </label>
              <select
                value={filters.difficulty}
                onChange={(e) => setFilters({...filters, difficulty: e.target.value})}
                className={inputClasses}
              >
                <option value="all">{t('faq.allDifficulties', 'Todas las dificultades')}</option>
                <option value="beginner">{t('faq.difficulties.beginner', 'Principiante')}</option>
                <option value="intermediate">{t('faq.difficulties.intermediate', 'Intermedio')}</option>
                <option value="advanced">{t('faq.difficulties.advanced', 'Avanzado')}</option>
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${textClasses} mb-2`}>
                {t('faq.motor', 'Motor')}
              </label>
              <select
                value={filters.motor}
                onChange={(e) => setFilters({...filters, motor: e.target.value})}
                className={inputClasses}
              >
                <option value="all">{t('faq.allMotors', 'Todos los motores')}</option>
                {getUniqueValues('motor').map(motor => (
                  <option key={motor} value={motor}>{motor}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${textClasses} mb-2`}>
                {t('faq.component', 'Componente')}
              </label>
              <select
                value={filters.component}
                onChange={(e) => setFilters({...filters, component: e.target.value})}
                className={inputClasses}
              >
                <option value="all">{t('faq.allComponents', 'Todos los componentes')}</option>
                {getUniqueValues('component').map(component => (
                  <option key={component} value={component}>{component}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className={`block text-sm font-medium ${textClasses} mb-2`}>
                {t('faq.sortBy', 'Ordenar por')}
              </label>
              <select
                value={filters.sort}
                onChange={(e) => setFilters({...filters, sort: e.target.value as any})}
                className={inputClasses}
              >
                <option value="relevance">{t('faq.sort.relevance', 'Relevancia')}</option>
                <option value="views">{t('faq.sort.views', 'Más vistas')}</option>
                <option value="helpful">{t('faq.sort.helpful', 'Más útiles')}</option>
                <option value="recent">{t('faq.sort.recent', 'Más recientes')}</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Categorías destacadas */}
      <div className={`${cardClasses} p-6 mb-6`}>
        <h2 className={`text-lg font-semibold ${textClasses} mb-4`}>
          {t('faq.exploreByCategory', 'Explorar por categoría')}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {categories.map((category) => {
            const Icon = category.icon;
            const count = faqs.filter(faq => faq && faq.category === category.id).length;
            
            return (
              <button
                key={category.id}
                onClick={() => setFilters({...filters, category: category.id})}
                className={`p-4 rounded-lg text-center transition-all duration-200 hover:shadow-md ${
                  filters.category === category.id
                    ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : `${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`
                }`}
              >
                <Icon className={`w-8 h-8 mx-auto mb-2 ${category.color.split(' ')[0]}`} />
                <div className={`font-medium text-sm ${textClasses}`}>
                  {category.name}
                </div>
                <div className={`text-xs ${subTextClasses} mt-1`}>
                  {count} pregunta{count !== 1 ? 's' : ''}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Estado de carga */}
      {loading && (
        <div className={`${cardClasses} p-8 text-center`}>
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className={textClasses}>{t('faq.loading', 'Cargando preguntas frecuentes...')}</p>
        </div>
      )}

      {/* Estado de error */}
      {error && (
        <div className={`${cardClasses} p-6 mb-6`}>
          <div className="flex items-center gap-3 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span>{t('faq.error', 'Error')}: {error}</span>
          </div>
        </div>
      )}

      {/* Estadísticas */}
      {!loading && !error && (
        <div className={`${cardClasses} p-4 mb-6`}>
          <div className="flex items-center justify-between">
            <div className={`text-sm ${subTextClasses}`}>
              {t('faq.showingResults', 'Mostrando {{showing}} de {{total}} preguntas frecuentes', {
                showing: filteredFAQs.length,
                total: faqs.length
              })}
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className={subTextClasses}>
                {t('faq.totalViews', 'Total visualizaciones: {{count}}', {
                  count: faqs.reduce((sum, faq) => sum + (faq.views || 0), 0)
                })}
              </div>
              <div className={subTextClasses}>
                {t('faq.totalHelpfulVotes', 'Total votos útiles: {{count}}', {
                  count: faqs.reduce((sum, faq) => sum + (faq.helpful_votes || 0), 0)
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lista de FAQs */}
      {!loading && !error && (
        <div className="space-y-4">
          {filteredFAQs.map((faq) => {
            const isExpanded = expandedFAQ === faq.id;
            const CategoryIcon = getCategoryIcon(faq.category);
            const helpfulnessPercentage = getHelpfulnessPercentage(faq);
            
            return (
              <div
                key={faq.id}
                className={`${cardClasses} overflow-hidden transition-all duration-200 ${
                  faq.is_featured ? 'ring-2 ring-yellow-400' : ''
                } ${isExpanded ? 'shadow-lg' : ''}`}
              >
                {/* Header de la FAQ */}
                <div
                  className={`p-6 cursor-pointer transition-colors duration-200 ${
                    isDark 
                      ? 'hover:bg-gray-700' 
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => toggleFAQ(faq.id)}
                >
                  <div className="flex items-start gap-4">
                    {/* Icono de categoría */}
                    <div className={`p-2 rounded-lg ${getCategoryColor(faq.category)}`}>
                      <CategoryIcon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      {/* Badges superiores */}
                      <div className="flex items-center gap-2 mb-3">
                        {faq.is_featured && (
                          <div className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 rounded-full text-xs">
                            <Star className="w-3 h-3" />
                            Destacada
                          </div>
                        )}
                        
                        <span className={`px-2 py-1 text-xs rounded-full ${getDifficultyColor(faq.difficulty)}`}>
                          {faq.difficulty === 'beginner' ? 'Principiante' : 
                           faq.difficulty === 'intermediate' ? 'Intermedio' : 'Avanzado'}
                        </span>
                        
                        <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(faq.category)}`}>
                          {faq.category}
                        </span>
                        
                        {faq.estimated_time && (
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            {faq.estimated_time}
                          </div>
                        )}
                      </div>
                      
                      {/* Pregunta */}
                      <h3 className={`text-lg font-semibold ${textClasses} mb-2 pr-8`}>
                        {faq.question}
                      </h3>
                      
                      {/* Metadatos */}
                      <div className="flex flex-wrap items-center gap-4 text-sm mb-3">
                        {faq.motor && (
                          <div className="flex items-center gap-1">
                            <Settings className="w-4 h-4 text-gray-400" />
                            <span className={subTextClasses}>Motor: {faq.motor}</span>
                          </div>
                        )}
                        
                        {faq.configuration && (
                          <div className="flex items-center gap-1">
                            <Wrench className="w-4 h-4 text-gray-400" />
                            <span className={subTextClasses}>Config: {faq.configuration}</span>
                          </div>
                        )}
                        
                        {faq.component && (
                          <div className="flex items-center gap-1">
                            <Target className="w-4 h-4 text-gray-400" />
                            <span className={subTextClasses}>Componente: {faq.component}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Estadísticas */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1">
                            <Eye className="w-4 h-4 text-gray-400" />
                            <span className={subTextClasses}>{faq.views || 0} vistas</span>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <ThumbsUp className="w-4 h-4 text-green-500" />
                            <span className={subTextClasses}>{faq.helpful_votes || 0}</span>
                          </div>
                          
                          {helpfulnessPercentage > 0 && (
                            <div className={`text-xs ${subTextClasses}`}>
                              {helpfulnessPercentage}% útil
                            </div>
                          )}
                          
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3 text-gray-400" />
                            <span className={`text-xs ${subTextClasses}`}>{faq.created_by}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contenido expandido */}
                {isExpanded && (
                  <div className={`border-t ${borderClasses} p-6`}>
                    
                    {/* Respuesta */}
                    <div className="mb-6">
                      <div className={`${textClasses} leading-relaxed`}>
                        {faq.answer.split('\n').map((paragraph, index) => {
                          if (paragraph.trim() === '') return null;
                          
                          return (
                            <div key={index} className="mb-4 last:mb-0">
                              {paragraph.includes('**') ? (
                                // Texto con formato markdown básico
                                <div 
                                  dangerouslySetInnerHTML={{ 
                                    __html: paragraph
                                      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
                                      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
                                      .replace(/`(.*?)`/g, `<code class="px-2 py-1 rounded text-sm font-mono ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'}">$1</code>`)
                                  }} 
                                />
                              ) : (
                                <p>{paragraph}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Partes relacionadas */}
                    {faq.related_parts && Array.isArray(faq.related_parts) && faq.related_parts.length > 0 && (
                      <div className="mb-6">
                        <h4 className={`font-medium ${textClasses} mb-3`}>
                          Partes relacionadas:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {faq.related_parts.map((part, index) => (
                            <span
                              key={index}
                              className={`px-3 py-1 rounded-md text-sm ${
                                isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {part}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    {faq.tags && Array.isArray(faq.tags) && faq.tags.length > 0 && (
                      <div className="mb-6">
                        <h4 className={`font-medium ${textClasses} mb-3`}>
                          Tags:
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {faq.tags.map((tag, index) => (
                            <span
                              key={index}
                              className={`px-2 py-1 rounded-full text-xs ${
                                isDark ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Información adicional */}
                    <div className={`text-sm ${subTextClasses} mb-6 p-3 rounded-lg ${
                      isDark ? 'bg-gray-750' : 'bg-gray-50'
                    }`}>
                      <div className="flex items-center gap-4 mb-2">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>Actualizado: {new Date(faq.updated_at).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>Por: {faq.created_by}</span>
                        </div>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm ${subTextClasses}`}>
                          {t('faq.wasHelpful', '¿Te resultó útil esta respuesta?')}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => voteFAQ(faq.id, true)}
                          className={`flex items-center gap-1 px-3 py-2 text-sm text-green-600 rounded-lg transition-colors ${
                            isDark 
                              ? 'hover:bg-green-900/20' 
                              : 'hover:bg-green-50'
                          }`}
                        >
                          <ThumbsUp className="w-4 h-4" />
                          {t('faq.helpful', 'Sí')} ({faq.helpful_votes || 0})
                        </button>
                        
                        <button
                          onClick={() => voteFAQ(faq.id, false)}
                          className={`flex items-center gap-1 px-3 py-2 text-sm text-red-600 rounded-lg transition-colors ${
                            isDark 
                              ? 'hover:bg-red-900/20' 
                              : 'hover:bg-red-50'
                          }`}
                        >
                          <ThumbsDown className="w-4 h-4" />
                          {t('faq.notHelpful', 'No')} ({faq.not_helpful_votes || 0})
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Mensaje cuando no hay FAQs */}
      {!loading && !error && filteredFAQs.length === 0 && (
        <div className={`${cardClasses} p-8 text-center`}>
          <Lightbulb className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className={`text-lg font-semibold ${textClasses} mb-2`}>
            {t('faq.noResults', 'No se encontraron preguntas frecuentes')}
          </h3>
          <p className={subTextClasses}>
            {t('faq.tryAdjustingFilters', 'Intenta ajustar los filtros o la búsqueda para encontrar más resultados.')}
          </p>
        </div>
      )}

      {/* FAQs destacadas flotante */}
      {faqs.filter(faq => faq.is_featured).length > 0 && !showFilters && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`${cardClasses} p-4 max-w-sm`}>
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-5 h-5 text-yellow-500" />
              <span className={`font-medium ${textClasses}`}>FAQs Destacadas</span>
            </div>
            <div className="space-y-2">
              {faqs
                .filter(faq => faq.is_featured)
                .slice(0, 3)
                .map(faq => (
                  <button
                    key={faq.id}
                    onClick={() => toggleFAQ(faq.id)}
                    className={`text-left text-sm ${subTextClasses} hover:text-blue-600 transition-colors line-clamp-2`}
                  >
                    {faq.question}
                  </button>
                ))
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQSection;